/**
 * Placement maths for {@link PinDirectionHintBanner}, kept out of the component
 * file so that module only exports components (fast-refresh friendly).
 * See [[pin-direction-hint.spec]] and [[directional-pins]].
 */

/**
 * When the pin sits in roughly the top fifth of the plan there is no room to
 * float the banner above the coachmark, so it drops below the pin instead.
 */
export const BANNER_FLIP_ABOVE_YPCT = 0.2;
/**
 * When the pin sits within this fraction of a left/right edge, a centred pill
 * (~half its width each side) would hang off that edge, so the pill is biased
 * inward — left-aligned to the pin near the left edge, right-aligned near the
 * right. Sized to the pill's `max-w-[15rem]` half-width over a typical plan.
 */
export const BANNER_EDGE_XPCT = 0.2;

/** Horizontal alignment of the pill relative to the pin. */
export type BannerAlign = "start" | "center" | "end";

/**
 * Resolves where the banner sits from the pin's image-relative position — a
 * coarse heuristic that keeps the pill on-screen near any edge (good at the
 * default zoom the ghost pin is placed at). Callers pass the result through.
 *
 * @param xPct - pin x in image-relative coords `[0, 1]`
 * @param yPct - pin y in image-relative coords `[0, 1]`
 */
export function bannerPlacement(
  xPct: number,
  yPct: number,
): { below: boolean; align: BannerAlign } {
  return {
    below: yPct < BANNER_FLIP_ABOVE_YPCT,
    align:
      xPct < BANNER_EDGE_XPCT
        ? "start"
        : xPct > 1 - BANNER_EDGE_XPCT
          ? "end"
          : "center",
  };
}
